import React, {useCallback, useMemo} from 'react';
import {Button, Stack} from '@mui/material';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import DownloadIcon from '@mui/icons-material/Download';
import NfcIcon from '@mui/icons-material/Nfc';
import {KeyTable, KeyTableActionContext} from "./KeyTable";
import {filterKeyTableRows, keyFilterSerializer, KeyTableFilter} from "./KeyTableFilter";
import {today} from "../../util/DateUtil";
import {Page, StorePageCount} from "../../components/page/Page";
import {EditBulkKeysForm} from "./EditBulkKeysForm";
import {useDeleteAction} from "../../util/actions/DeleteAction";
import {useFormDialogAction} from "../../components/form-dialog/FormDialogProvider";
import {EditKeyForm} from "./KeyFormDialog";
import {useFilter} from "../../util/FilterSerializer";
import {KeyData, KeyVault} from "../../api/Model";
import {downloadKeyCsv} from "./ExportKeyCsv";
import {LoadingData} from "../../components/loading-data/LoadingData";
import {useMessage} from "../../util/actions/Message";
import SyncReleezmeButton from "./SyncReleezmeButton";
import {NfcDataRow, UploadNfcDataForm} from "./UploadNfcDataForm";
import {useKeyDataStore} from "../../api/KeyDataStore";
import {useKeyProblemStore} from "../../api/KeyProblemStore";
import {RemoteValue} from "../../util/RemoteValue";
import {usePermission} from "../../util/access/UseAccess";
import {KeyData_Admin, KeyData_Operate} from "../../util/access/Permission";
import {useEmployeeMapStore} from "../../api/EmployeeMapStore";
import {ManageKeyIntegrationSettings} from "./ManageKeyIntegrationSettings";
import ImportKeysButton from "./ImportKeysButton";


function ManageKeyPage({defaultKeyVault, keyVaults}: {
    defaultKeyVault: KeyVault,
    keyVaults: KeyVault[],
}) {
    const message = useMessage();
    const {
        remoteValue: unfilteredRows,
        createKeyRange,
        updateKey,
        deleteKey,
        bindNFC,
    } = useKeyDataStore();
    const employeeMap = useEmployeeMapStore(state => state.remoteValue);
    const keyProblems = useKeyProblemStore(state => state.remoteValue);
    const filterSerializer = useMemo(
        () => keyFilterSerializer(keyVaults),
        [keyVaults]
    );
    const [filter, setFilter] = useFilter(filterSerializer);
    const {canOperate, canAdmin} = usePermission({
        canOperate: KeyData_Operate,
        canAdmin: KeyData_Admin,
    });

    const filtered = useMemo(() => {
        return RemoteValue.combine({unfilteredRows, keyProblems})
            .map(({unfilteredRows, keyProblems}) => {
                const filteredRows = filterKeyTableRows(unfilteredRows, keyProblems, filter);
                return ({
                    rows: filteredRows,
                    problems: keyProblems,
                    length: filteredRows.length,
                });
            });
    }, [filter, unfilteredRows, keyProblems]);

    const handleExportCsv = useCallback(async () => {
        const keys = (await filtered.value).rows;
        if (keys.length > 0) {
            downloadKeyCsv(keys, await employeeMap.value, filter.searchIn === 'full-history');
        } else {
            message.error('No keys found to export.');
        }
    }, [filtered, message, filter]);

    const handleUploadNfc = useFormDialogAction({
        title: 'Upload NFC Data',
        description: 'Upload a CSV file with "Key Number" and "NFC" columns to bulk-update transponder IDs. Scan a key with the NFC reader to highlight the matching row.',
        submitButtonText: 'Update',
        fullWidth: true,
        renderForm: UploadNfcDataForm,
        action: openDialog => openDialog({
            initialValue: [] as NfcDataRow[],
            onSubmit: async (rows: NfcDataRow[]) => {
                for (const row of rows) {
                    if (row.resolvedKey) {
                        await bindNFC(row.resolvedKey, row.uploadedTransponderId);
                    }
                }
            },
            successMessage: (rows: NfcDataRow[]) => `Updated transponder IDs for ${rows.filter(r => r.resolvedKey).length} key(s)`,
            errorMessage: 'Failed to update transponder IDs',
        }),
    }, [updateKey]);

    const handleCreateKeysRange = useFormDialogAction({
        title: 'Create Keys',
        description: 'Enter the key ranges to add multiple keys at once.',
        submitButtonText: 'Create',
        renderForm: EditBulkKeysForm,
        action: openDialog => openDialog({
            initialValue: {
                keyRanges: [{
                    id: 0,
                    start: undefined,
                    end: undefined,
                }],
                record: {
                    from: today(),
                    to: undefined,
                    location: {type: 'with-vault'},
                    vault: defaultKeyVault,
                    notes: undefined,
                },
            },
            onSubmit: createKeyRange,
            successMessage: 'Created new keys',
            errorMessage: 'Failed to create new keys. Please try again later.',
        })
    }, [createKeyRange]);

    const handleUpdateKey = useFormDialogAction({
        title: 'Edit Key',
        description: 'Update the key information below.',
        submitButtonText: 'Update',
        renderForm: EditKeyForm,
        action: (openDialog, key: KeyData) => openDialog({
            initialValue: {
                keyNumber: key.keyNumber,
                transponderId: key.transponderId || null,
                tags: key.tags,
            },
            onSubmit: form => updateKey(key, form),
            successMessage: form => `Updated key ${form.keyNumber}`,
            errorMessage: 'Failed to update key. Please try again later.',
        })
    }, [updateKey]);

    const handleDeleteKey = useDeleteAction({
        description: 'Are you sure you want to delete this key? This action cannot be undone and will remove all history records associated with this key.',
        action: deleteKey,
        successMessage: key => `Deleted key ${key.keyNumber}`,
        failureMessage: 'Failed to delete key',
    }, [deleteKey]);

    return (
        <Page
            header="Manage Keys"
            counter={<StorePageCount totalValue={unfilteredRows} value={filtered}/>}
            actions={canAdmin ? (
                <Stack direction="row" spacing={2}>
                    <ImportKeysButton/>
                    <SyncReleezmeButton/>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<DownloadIcon/>}
                        onClick={handleExportCsv}
                    >
                        Export
                    </Button>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<NfcIcon/>}
                        onClick={handleUploadNfc}
                    >
                        Upload NFC
                    </Button>
                    <ManageKeyIntegrationSettings/>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<PlaylistAddIcon/>}
                        onClick={handleCreateKeysRange}
                    >
                        Add Keys
                    </Button>
                </Stack>
            ) : null}
            filter={
                <KeyTableFilter
                    filter={filter}
                    setFilter={setFilter}
                />
            }
        >
            <LoadingData
                totalValue={unfilteredRows}
                value={filtered}
                emptyMessage="No keys found."
                noMatchMessage="No keys match the current filters. Try adjusting your filter criteria."
                defaultErrorMessage="Failed to load key data. Please try again later."
            >{filtered =>
                <KeyTableActionContext value={{
                    problems: filtered.problems,
                    onEditKey: handleUpdateKey,
                    onDeleteKey: handleDeleteKey,
                    canOperate,
                    canAdmin,
                }}>
                    <KeyTable keys={filtered.rows}/>
                </KeyTableActionContext>
            }</LoadingData>
        </Page>
    );
}

export default ManageKeyPage;
